/**
 * Auth API — login, register, profile, logout
 */

import apiClient, { tokenStore } from './client';

export interface LoginResponse {
    access: string;
    refresh: string;
    user: {
        id: number;
        email: string;
        full_name: string;
        role: 'customer' | 'vendor' | 'admin' | 'superadmin';
        avatar: string | null;
    };
}

export interface RegisterData {
    email: string;
    password: string;
    full_name: string;
    phone?: string;
    country_code?: string;
    role?: 'customer' | 'vendor';
}

export const authApi = {
    login: async (email: string, password: string): Promise<LoginResponse> => {
        const { data } = await apiClient.post<LoginResponse>('/auth/login/', { email, password });
        tokenStore.set(data.access, data.refresh);
        return data;
    },

    register: async (payload: RegisterData): Promise<LoginResponse> => {
        const { data } = await apiClient.post<LoginResponse>('/auth/register/', payload);
        tokenStore.set(data.access, data.refresh);
        return data;
    },

    logout: async () => {
        try {
            const refresh = tokenStore.getRefresh();
            if (refresh) await apiClient.post('/auth/logout/', { refresh });
        } finally {
            tokenStore.clear();
        }
    },

    getMe: async () => {
        const { data } = await apiClient.get('/auth/me/');
        return data;
    },

    updateMe: async (payload: Partial<RegisterData & { avatar: File }>) => {
        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
            if (v !== undefined) formData.append(k, v as string | Blob);
        });
        const { data } = await apiClient.patch('/auth/me/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    },

    // Vendor store management
    getMyStore: async () => {
        const { data } = await apiClient.get('/auth/stores/');
        return data.results?.[0] || null;
    },

    createStore: async (payload: object) => {
        const { data } = await apiClient.post('/auth/stores/', payload);
        return data;
    },

    requestPOSAccess: async (reason: string) => {
        const { data } = await apiClient.post('/auth/pos-requests/', { reason });
        return data;
    },
};
